import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaHourglassHalf, FaTimesCircle } from "react-icons/fa";
import { useParams } from "react-router-dom";

import WhiteCurl from "../../../assets/white_curl.svg";
import LoadingSpinner from "../../../components/LoadingSpinner";
import lessonServices from "../../../services/lessonServices";
import studentServices from "../../../services/studentServices";
import { getLanguage } from "../../../utils/getLanguage";

const STATUS_MAP = {
  2: { label: "submitted", icon: <FaHourglassHalf />, color: "text-blue-500" },
  3: { label: "approved", icon: <FaCheckCircle />, color: "text-green-500" },
  4: { label: "rejected", icon: <FaTimesCircle />, color: "text-red-500" },
  5: { label: "resubmitted", icon: <FaHourglassHalf />, color: "text-orange-500" },
};

const StudentLevelProject = () => {
  const { t } = useTranslation();
  const { id: lessonID } = useParams();

  const lang = getLanguage();
  const fileInputRef = useRef();

  const [uploadedFile, setUploadedFile] = useState(null);
  const [status, setStatus] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchLessonProject = async () => {
    setLoading(true);

    try {
      const response = await lessonServices.getStudentLessonProject(lessonID);
      setUploadedFile(response?.file_submitted || null);
      setStatus(response ? (response.last_status_id ?? 1) : 1); // Treat null/undefined as PENDING
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonProjectDetails = async () => {
    setLoading(true);
    try {
      const response = await lessonServices.getStudentLessonProjectDetails(lessonID);
      setProjectDetails(response);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleTriggerUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setLoadingSubmit(true);
      await studentServices.submitLessonProject(lessonID, formData);
      await lessonServices.finishLesson(lessonID);
      await fetchLessonProject();
      await fetchLessonProjectDetails();
    } catch (error) {
      console.error("Submit failed:", error);
    } finally {
      setLoadingSubmit(false);
    }
  };

  useEffect(() => {
    fetchLessonProject();
    fetchLessonProjectDetails();
  }, [lang]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      {/* Header with status */}
      <div className="mb-5 flex items-center gap-6">
        <h1 className="text-3xl font-bold text-blue-700">{projectDetails?.name}</h1>
        <p className="flex items-center justify-center gap-1 text-sm font-light">
          {STATUS_MAP[status] && (
            <span className={`flex items-center gap-1 text-xl capitalize ${STATUS_MAP[status].color}`}>
              {STATUS_MAP[status].icon}
              {t(STATUS_MAP[status].label)}
            </span>
          )}
        </p>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-6 gap-10">
        <div className="col-span-3">
          <div className="space-y-3 text-2xl text-blue-700">
            <p>{projectDetails?.challenge_statement}</p>
          </div>
        </div>

        <div className="col-span-3">
          <div className="relative aspect-video w-full rounded-3xl border-2 border-[#96B8F3]">
            <img
              src={projectDetails?.image}
              alt="Lesson"
              className="h-full w-full rounded-3xl object-cover"
            />
          </div>
        </div>
      </div>

      {/* Upload & Submit section */}
      {(status === 1 || status === 4) && (
        <div className="mt-10 flex flex-col items-center">
          <div className="flex w-full max-w-2xl flex-row flex-wrap items-center justify-center gap-x-6 gap-y-4">
            {/* Upload Button */}
            {uploadedFile || selectedFile ? (
              <button
                onClick={handleTriggerUpload}
                className="wavy-btn relative min-w-[250px] flex-1 rounded-3xl border-4 border-[#ffe4af] bg-orange-500 p-2 text-white"
              >
                <span className="absolute left-[6px] top-[6px]">
                  <img src={WhiteCurl} className="w-5" alt="" />
                </span>
                <div className="text-center">
                  <p className="truncate font-semibold">{t("1_file_uploaded")}</p>
                  <p className="mt-1 cursor-pointer text-sm font-light underline">
                    {t("click_here_to_change_it")}{" "}
                  </p>
                </div>
              </button>
            ) : (
              <button
                onClick={handleTriggerUpload}
                className="wavy-btn relative min-w-[250px] flex-1 rounded-3xl border-4 border-[#ffe4af] bg-orange-500 p-4 text-lg font-semibold text-white transition duration-200 hover:bg-orange-600"
              >
                <span className="absolute left-[6px] top-[6px]">
                  <img src={WhiteCurl} className="w-5" alt="" />
                </span>
                {t("upload_file")}
              </button>
            )}

            {/* Submit Button */}
            {(uploadedFile || selectedFile) && (
              <button
                className="wavy-btn relative min-w-[250px] flex-1 rounded-3xl border-4 border-[#96B8F3] bg-blue-700 p-4 text-lg font-semibold text-white transition duration-200 hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleSubmit}
                disabled={loadingSubmit || !selectedFile}
              >
                <span className="absolute left-[6px] top-[6px]">
                  <img src={WhiteCurl} className="w-5" alt="" />
                </span>
                {loadingSubmit ? (
                  <div className="flex items-center justify-center">
                    <span className="inline-block h-7 w-7 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  </div>
                ) : (
                  t("submit")
                )}
              </button>
            )}
            <input
              type="file"
              accept=".zip,.pdf,.doc,.docx,.mp4,.jpg,.jpeg,.png,.gif,.webp"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentLevelProject;
